import React, { useState, useEffect, useCallback } from 'react';
import { ProfileLink } from '@/components/profile/ProfileLink';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDistanceToNow } from 'date-fns';
import { Loader2, Send } from 'lucide-react';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface Comment {
  id: string;
  content: string;
  parentId: string | null;
  createdAt: string;
  author: {
    id: string;
    name: string;
    initials: string;
    image?: string | null;
  };
}

interface PostCommentsDialogProps {
  postId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCommentAdded?: () => void;
  communityId?: string;
}

export function PostCommentsDialog({
  postId,
  open,
  onOpenChange,
  onCommentAdded,
  communityId: _communityId,
}: PostCommentsDialogProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewport, setViewport] = useState(() => ({
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
    offsetTop: 0,
  }));

  const syncViewport = useCallback(() => {
    const vv = window.visualViewport;
    if (vv) {
      setViewport({ height: vv.height, offsetTop: vv.offsetTop });
      return;
    }
    setViewport({ height: window.innerHeight, offsetTop: 0 });
  }, []);

  useEffect(() => {
    if (!open) return;
    syncViewport();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', syncViewport);
    vv?.addEventListener('scroll', syncViewport);
    window.addEventListener('resize', syncViewport);
    return () => {
      vv?.removeEventListener('resize', syncViewport);
      vv?.removeEventListener('scroll', syncViewport);
      window.removeEventListener('resize', syncViewport);
    };
  }, [open, syncViewport]);

  useEffect(() => {
    if (open && postId) {
      const fetchComments = async () => {
        setIsLoading(true);
        try {
          const { data } = await axiosInstance.get(`/api/posts/${postId}/comments`);
          setComments(Array.isArray(data) ? data : []);
        } catch (error) {
          console.error(error);
          toast.error('Failed to load comments');
        } finally {
          setIsLoading(false);
        }
      };

      fetchComments();
    }
  }, [open, postId]);

  const refreshComments = async () => {
    try {
      const { data } = await axiosInstance.get(`/api/posts/${postId}/comments`);
      setComments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setIsSubmitting(true);
    try {
      await axiosInstance.post(`/api/posts/${postId}/comments`, {
        content: newComment,
      });
      setNewComment('');
      await refreshComments();
      onCommentAdded?.();
      toast.success('Comment posted');
    } catch (error) {
      console.error(error);
      toast.error('Failed to post comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const dialogMaxHeight = Math.max(240, Math.min(viewport.height - 16, viewport.height * 0.9));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'sm:max-w-[500px] w-[calc(100%-2rem)] flex flex-col p-0 gap-0 overflow-hidden',
          'max-h-none translate-x-[-50%] translate-y-[-50%]'
        )}
        style={{
          maxHeight: dialogMaxHeight,
          height: dialogMaxHeight,
          top: viewport.offsetTop + viewport.height / 2,
        }}
      >
        <DialogHeader className="px-6 py-4 border-b border-border shrink-0">
          <DialogTitle>Comments</DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 overscroll-contain">
          {isLoading ? (
            <div className="flex justify-center p-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center text-muted-foreground py-8 text-sm">
              No comments yet. Be the first to say something!
            </div>
          ) : (
            <div className="space-y-6">
              {comments.map((comment) => (
                <div key={comment.id} className="flex gap-3">
                  <ProfileLink
                    userId={comment.author.id}
                    image={comment.author.image || undefined}
                    name={comment.author.name}
                    showName={false}
                    avatarSize="sm"
                  />
                  <div className="flex-1 space-y-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <ProfileLink
                        userId={comment.author.id}
                        showAvatar={false}
                        className="text-sm font-semibold hover:underline truncate"
                      >
                        {comment.author.name}
                      </ProfileLink>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-sm text-foreground whitespace-pre-wrap break-words">
                      {comment.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-border shrink-0 bg-background">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onFocus={(e) => {
                const target = e.currentTarget;
                window.setTimeout(() => {
                  syncViewport();
                  target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }, 300);
              }}
              placeholder="Write a comment..."
              disabled={isSubmitting}
              className="flex-1"
              enterKeyHint="send"
            />
            <Button type="submit" size="icon" disabled={isSubmitting || !newComment.trim()}>
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
